import { AsyncPipe } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CmsService, RoutingService, UserTypeService } from '@boels/helpers';
import { BOProduct, CmsParagraphComponent, Page } from '@boels/models';
import { PrepareHtmlPipe, ProductFacade } from '@boels/shared-state';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, startWith } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'boels-product-price-disclaimer',
  templateUrl: './product-price-disclaimer.component.html',
  styleUrls: ['./product-price-disclaimer.component.scss'],
  imports: [PrepareHtmlPipe, AsyncPipe],
})
export class ProductPriceDisclaimerComponent {
  /** Injected services */
  #userTypeService = inject(UserTypeService);
  #translationService = inject(TranslocoService);
  #routingService = inject(RoutingService);
  #cmsService = inject(CmsService);
  #productFacade = inject(ProductFacade);

  $product = input.required<BOProduct>();
  contentData$: Observable<string> = toObservable(this.$product).pipe(
    switchMap((product) => {
      if (!Object.keys(product).includes('tax'))
        return this.#productFacade
          .get(product.code as string)
          .pipe(
            switchMap((product) =>
              this.#enrichDisclaimerText(product as BOProduct),
            ),
          );

      return this.#enrichDisclaimerText(product as BOProduct);
    }),
  );

  readonly #taxKey = 'tax';
  readonly #taxTextKey = 'taxApplyingText';

  #enrichDisclaimerText(product: BOProduct): Observable<string> {
    return this.getVatTextForUserType().pipe(
      map((vatText) => {
        const page = this.#routingService.getRoutePropertyWithValue<
          Page,
          'data'
        >('data', 'routeResolver');
        const data = page && this.#getComponentData(page);
        return data?.content
          ? this.#triggerReplaceText(
              product as BOProduct,
              data.content,
              vatText,
            )
          : '';
      }),
    );
  }

  #getComponentData(page: Page): CmsParagraphComponent | void {
    const slot = this.#cmsService.getSlotByPosition(page, 'PriceDisclaimer');
    if (slot) {
      return this.#cmsService.getComponentData(
        slot,
        'PriceDisclaimerComponent',
      );
    }
  }

  #triggerReplaceText(
    product: BOProduct,
    content: string,
    vatText: string,
  ): string {
    const text = product?.tax?.formattedValue
      ? this.replaceText(content, this.#taxKey, product?.tax?.formattedValue)
      : content;
    return this.replaceText(text, this.#taxTextKey, vatText);
  }

  getVatTextForUserType(): Observable<string> {
    return this.#userTypeService.onUserTypeChange().pipe(
      startWith(undefined),
      switchMap(() => {
        return this.#userTypeService.isIndividualUser()
          ? this.#translationService.selectTranslate(
              'productDetails.priceDisclaimer.textInclVat',
            )
          : this.#translationService.selectTranslate(
              'productDetails.priceDisclaimer.textExclVat',
            );
      }),
    );
  }

  replaceText(content: string, key: string, value: string): string {
    const regex = new RegExp(`{{${key}}}`, 'g');
    return content?.replace(regex, value);
  }
}
